// FilterModal.tsx
import React, { useMemo, useState } from "react";
import styles from './FilterSection.module.css';
import CategoryPicker from './CategoryPicker';
import PeriodPicker from './PeriodPicker';
import { LocateMe } from './LocateMe';
import { BrowseFilterState, getDefaultBrowseFilters, getMobileFilterSummary, getAppliedFilterChips } from '../utils/filterUtils';
import { useCategories } from '../hooks/useCategories';

interface FilterModalProps {
  hide: () => void;
  filters: BrowseFilterState;
  onApply: (filters: BrowseFilterState) => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({ hide, filters, onApply }) => {
  const defaults = getDefaultBrowseFilters();
  const { categories: availableCategories } = useCategories();
  const [draftFilters, setDraftFilters] = useState(filters);

  const categoryNameBySlug = useMemo(() => {
    return new Map(availableCategories.map((category) => [category.slug, category.name]));
  }, [availableCategories]);

  const summary = useMemo(() => {
    return getMobileFilterSummary(getAppliedFilterChips(draftFilters, categoryNameBySlug));
  }, [draftFilters, categoryNameBySlug]);

  const updateDraft = (patch: Partial<BrowseFilterState>) => {
    setDraftFilters((current) => ({ ...current, ...patch }));
  };

  const toggleFree = () => {
    setDraftFilters((current) => ({
      ...current,
      free: current.free === '1' ? '0' : '1',
    }));
  };

  const handleClearAll = () => {
    setDraftFilters((current) => ({
      ...current,
      categories: defaults.categories,
      free: defaults.free,
      period: defaults.period,
      time: defaults.time,
      venue: defaults.venue,
      latitude: defaults.latitude,
      longitude: defaults.longitude,
    }));
  };

  const handleApply = () => {
    onApply(draftFilters);
    hide();
  };

  return (
    <div className={styles.filterExpanded}>
      <div className={styles.filterModalHeader}>
        <div>
          <h1>Filtres</h1>
          <p className={styles.filterModalSummary}>{summary}</p>
        </div>
        <button type="button" className={styles.filterInlineClear} onClick={handleClearAll}>
          Tout effacer
        </button>
      </div>

      <div className={styles.filterModalBody}>
        <CategoryPicker
          selectedCategories={draftFilters.categories.split(';')}
          onChange={(cats) => updateDraft({ categories: cats.join(';') })}
        />
        <LocateMe
          latitude={draftFilters.latitude}
          longitude={draftFilters.longitude}
          onLocationChange={(latitude, longitude) => updateDraft({ latitude, longitude })}
        />
        <button
          type="button"
          className={[styles.freeButton, draftFilters.free === '1' ? styles.selected : ''].join(' ')}
          onClick={toggleFree}
          aria-pressed={draftFilters.free === '1'}
        >
          Gratuit
          {draftFilters.free === '1' && <span className={styles.filterCheckBadge} aria-hidden="true">✓</span>}
        </button>
        <PeriodPicker
          period={draftFilters.period}
          time={draftFilters.time}
          onPeriodChange={(period) => updateDraft({ period })}
          onTimeChange={(time) => updateDraft({ time })}
        />
      </div>

      <div className={styles.filterModalFooter}>
        <button type="button" className={styles.filterSecondaryAction} onClick={hide}>Fermer</button>
        <button type="button" className={styles.filterPrimaryAction} onClick={handleApply}>Appliquer</button>
      </div>
    </div>
  );
};
